import React from 'react';
import Box from '@mui/material/Box';
import { deserializeTransaction } from '@stacks/transactions';
import classNames from 'classnames';
import { isNull } from 'lodash';
import { TransactionStatus } from 'store/types';
import { getEventFromTransferMemo } from 'utils/stx20util';

import useSafe from '../../../../hooks/use-safe';
import { formatDate } from '../../../../utils/dateUtils';
import { isContractCallTx } from '../../../../utils/post-condition.utils';
import { FunctionArguments, PostConditions, TransactionInfoInline } from '../../../TransactionList';
import { WalletWithIcons } from '../../../Wallet/WalletWithIcons/WalletWithIcons';
import { TransactionCommonDialog } from '../TransactionCommonDialog';

import { useHistoryDetailsDialogStyles } from './useHistoryDetailsDialogStyles';

interface TransactionCommonProps {
  transactionId: string;
  isProcessing?: boolean;
  onClose: () => void;
}

export const TransactionDetailsDialog = (props: TransactionCommonProps) => {
  const { transactionId, onClose, isProcessing } = props;
  const classes = useHistoryDetailsDialogStyles();

  const { transactions, safe } = useSafe();

  const transaction = transactions.find((transaction) => transaction._id === transactionId);
  const isExecuted = transaction?.status === TransactionStatus.EXECUTED;
  const isRejected = transaction?.status === TransactionStatus.REJECTED;
  const isErrored = transaction?.status === TransactionStatus.EXECUTION_ERRORED;
  const isOnSign = !isExecuted && !isErrored && !isRejected && !isProcessing;
  const isFullySigned = isOnSign && (transaction?.signatures?.length || 0) >= safe.threshold;

  const desirializedTx = deserializeTransaction(transaction?.rawTransaction || '');
  const memo = (desirializedTx as any).payload.memo;
  const memoEvent = memo && getEventFromTransferMemo(memo.content.replaceAll('\u0000', ''));

  const getStatusText = () => {
    if (isProcessing)
      return transaction?.status === TransactionStatus.EXECUTION ? 'Finalizing' : 'Processing';
    if (isExecuted) return 'Executed';
    if (isRejected) return 'Rejected';
    if (isErrored) return 'Execution Error';
    if (isOnSign) return `${transaction?.signatures.length}/${safe.threshold} confirmations`;

    return '';
  };

  if (!transaction) return <></>;

  return (
    <TransactionCommonDialog
      open={!isNull(transaction)}
      title="Transaction details"
      button=""
      onClose={onClose}
      fromBottom
    >
      <div className={classes.wrapper}>
        <div className={classes.detailsWrapper}>
          {isContractCallTx(transaction) && (
            <>
              <PostConditions transaction={transaction} />
              <FunctionArguments transaction={transaction} />
            </>
          )}
          <div className={classes.itemWrapper}>
            <span className={classes.itemTitle}>Transaction nonce</span>
            <span className={classes.itemContent}>{transaction.nonce}</span>
          </div>
          <TransactionInfoInline transaction={transaction} memoEvent={memoEvent} />
          <div className={classes.itemWrapper}>
            <span className={classes.itemTitle}>Initiator address</span>
            <span className={classes.itemContent}>
              <WalletWithIcons address={transaction.creator} />
            </span>
          </div>
          {isExecuted && (
            <div className={classes.itemWrapper}>
              <span className={classes.itemTitle}>Transaction ID</span>
              <span className={classes.itemContent}>
                <WalletWithIcons address={transaction.txId || ''} isTxn />
              </span>
            </div>
          )}
          <div className={classes.itemWrapper}>
            <span className={classes.itemTitle}>Date</span>
            <span className={classes.itemContent}>{formatDate(transaction.create_time)}</span>
          </div>
          <div className={classes.itemWrapper}>
            <span className={classes.itemTitle}>Fee</span>
            <span className={classes.itemContent}>
              {Number(transaction.fee) / Math.pow(10, 6)}
              {' STX'}
            </span>
          </div>
          {Boolean(transaction.notes) && (
            <div className={classes.itemWrapper}>
              <span className={classes.itemTitle}>Notes</span>
              <span className={classes.itemContent}>{transaction.notes}</span>
            </div>
          )}

          <div className={classes.itemWrapper}>
            <span className={classes.itemTitle}>Status</span>
            <span className={classes.itemContent}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '24px', gap: '4px' }}>
                <Box
                  component="span"
                  className={classNames(
                    classes.confirmations,
                    (isExecuted || isFullySigned) && classes.confirmationComplete,
                    (isErrored || isRejected) && classes.confirmationRejected
                  )}
                >
                  {getStatusText()}
                </Box>
              </Box>
            </span>
          </div>
        </div>
      </div>
    </TransactionCommonDialog>
  );
};
